"use client";

import { Form, Input, Button, message } from "antd";
import { useTranslation } from "react-i18next";
import "./style.css";
import { useCreateContactMutation } from "~/hooks/contact/useContactForm";

interface ContactForm {
  name: string;
  email: string;
  message: string;
  phone: string;
  subject: string;
}

export const ContactView = () => {
  const [form] = Form.useForm();
  const { createContact, isLoading } = useCreateContactMutation();
  const { t } = useTranslation('contact');

  const handleSubmit = async (values: ContactForm) => {
    try {
      await createContact(values);
      message.success(t('info.success'));
      form.resetFields();
    } catch (error) {
      console.error(error);
      message.error(t('info.error'));
    }
  };

  return (
    <div className="dev-contact-page">
      <div className="dev-contact-container">

        {/* Header */}
        <div className="dev-contact-header">
          <span className="dev-section-comment">{t('info.page_comment')}</span>
          <h1 className="dev-contact-title">{t('info.page_title')}</h1>
          <p className="dev-contact-subtitle">
            {t('info.page_subtitle')}
          </p>
        </div>

        <div className="dev-contact-grid">
          {/* FORM */}
          <div className="dev-contact-form-wrapper">
            <div className="dev-contact-form-header">
              {t('info.send_title')}
            </div>

            <Form
              layout="vertical"
              form={form}
              onFinish={handleSubmit}
              className="dev-contact-form"
            >
              <Form.Item
                label={t('info.name_label')}
                name="name"
                rules={[{ required: true, message: t('info.name_required') }]}
              >
                <Input placeholder={t('info.name_placeholder')} className="dev-input" />
              </Form.Item>

              <div className="dev-form-row">
                <Form.Item
                  label={t('info.email_label')}
                  name="email"
                  style={{ flex: 1 }}
                  rules={[
                    { required: true, message: t('info.email_required') },
                    { type: "email", message: t('info.email_invalid') },
                  ]}
                >
                  <Input type="email" placeholder={t('content.email_placeholder')} className="dev-input" />
                </Form.Item>
                <Form.Item
                  label={t('info.phone_label')}
                  name="phone"
                  style={{ flex: 1 }}
                  rules={[
                    { required: true, message: t('info.phone_required') },
                    { pattern: /^[0-9]{10}$/, message: t('info.phone_invalid') },
                  ]}
                >
                  <Input placeholder={t('info.phone_placeholder')} className="dev-input" />
                </Form.Item>
              </div>

              <Form.Item
                label={t('info.subject_label')}
                name="subject"
                rules={[{ required: true, message: t('info.subject_required') }]}
              >
                <Input placeholder={t('info.subject_placeholder')} className="dev-input" />
              </Form.Item>

              <Form.Item label={t('info.message_label')} name="message">
                <Input.TextArea
                  placeholder={t('info.message_placeholder')}
                  rows={5}
                  className="dev-input"
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                className="dev-contact-submit"
                block
              >
                {t('info.send_btn')}
              </Button>
            </Form>
          </div>

          {/* INFO + MAP */}
          <div className="dev-contact-info-wrapper">
            <div className="dev-contact-info-card">
              <div className="dev-contact-form-header" style={{ marginBottom: 20 }}>
                {t('info.info_title')}
              </div>

              <div className="dev-info-row">
                <div className="dev-info-icon">✉</div>
                <div className="dev-info-text">
                  <div className="dev-info-label">{t('info.email_label')}</div>
                  <a href="mailto:dat.nguyen@apetechs.com">dat.nguyen@apetechs.com</a>
                  <br />
                  <a href="mailto:info@apetechs.com">info@apetechs.com</a>
                </div>
              </div>

              <div className="dev-info-row">
                <div className="dev-info-icon">📞</div>
                <div className="dev-info-text">
                  <div className="dev-info-label">{t('info.hotline_label')}</div>
                  <a href="tel:0987183484">0987 183 484</a>

                </div>
              </div>

              <div className="dev-info-row">
                <div className="dev-info-icon">📍</div>
                <div className="dev-info-text">
                  <div className="dev-info-label">{t('info.address_label')}</div>
                  <a
                    href="https://maps.app.goo.gl/95NVBE38PzmBMVcU8"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('info.address')}
                  </a>
                </div>
              </div>
            </div>

            <div className="dev-contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4766.3596258849075!2d106.77028197584103!3d10.79341158935638!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31752686502e063f%3A0x3f5d2e5d57319999!2zQ8O0bmcgVHkgQ-G7lSBQaOG6p24gQXBldGVjaHM!5e1!3m2!1sen!2s!4v1756096611657!5m2!1sen!2s"
                loading="lazy"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={false}
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
